import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Filter, Plus, Play, X, Loader2 } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

const TIPOS = [
    "monto_maximo", "monto_minimo", "dpd_maximo", "seasoning_minimo",
    "concentracion_deudor_pct", "concentracion_sector_pct",
    "plazo_maximo", "tasa_minima", "moneda_permitida",
    "excluir_castigado", "custom_expression",
];
const SEVERIDADES = ["hard", "soft"];

function tipoDefaults(tipo) {
    if (tipo === "monto_maximo" || tipo === "monto_minimo") return { monto: 1000000 };
    if (tipo === "dpd_maximo") return { dias: 60 };
    if (tipo === "plazo_maximo") return { meses: 36 };
    if (tipo === "tasa_minima") return { tasa: 0.10 };
    if (tipo === "moneda_permitida") return { monedas: ["MXN"] };
    if (tipo === "concentracion_deudor_pct" || tipo === "concentracion_sector_pct") return { pct: 10 };
    return {};
}

function money(n, cur = "MXN") {
    return Number(n || 0).toLocaleString("es-MX", { style: "currency", currency: cur, maximumFractionDigits: 0 });
}

export default function ElegibilidadTab({ facility }) {
    const { hasRole } = useAuth();
    const [activa, setActiva] = useState({ reglas: [], version: 0 });
    const [cortes, setCortes] = useState([]);
    const [edit, setEdit] = useState(false);
    const [reglasEdit, setReglasEdit] = useState([]);
    const [motivo, setMotivo] = useState("");
    const [corteId, setCorteId] = useState("");
    const [evaluacion, setEvaluacion] = useState(null);
    const [historico, setHistorico] = useState([]);
    const [busy, setBusy] = useState(false);

    const canEdit = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.RIESGOS, ROLES.ADMIN_TI]);

    const load = async () => {
        const [{ data: a }, { data: c }, { data: h }] = await Promise.all([
            api.get(`/facilities/${facility.id}/reglas-elegibilidad/activa`),
            api.get("/tape/cortes"),
            api.get(`/facilities/${facility.id}/evaluaciones-elegibilidad`),
        ]);
        setActiva(a);
        const cs = (c || []).filter((x) => x.facility_id === facility.id);
        setCortes(cs);
        if (!corteId && cs.length) setCorteId(cs[0].corte_id);
        setHistorico(h);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const startEdit = () => {
        setReglasEdit((activa.reglas || []).map((r) => ({ ...r })));
        setEdit(true);
    };

    const addRegla = () => setReglasEdit((r) => [...r, {
        codigo: `REG_${r.length + 1}`, nombre: "", tipo: "monto_maximo",
        parametros: tipoDefaults("monto_maximo"), severidad: "hard", haircut_pct: null,
    }]);

    const rmRegla = (i) => setReglasEdit((r) => r.filter((_, idx) => idx !== i));

    const setRegla = (i, k, v) => setReglasEdit((r) => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

    const saveReglas = async () => {
        for (const r of reglasEdit) {
            if (!r.codigo || !r.nombre) return toast.error("Todas las reglas requieren código y nombre");
            if (r.severidad === "soft" && (!r.haircut_pct && r.haircut_pct !== 0)) {
                return toast.error(`Regla ${r.codigo} soft requiere haircut_pct`);
            }
        }
        setBusy(true);
        try {
            await api.post(`/facilities/${facility.id}/reglas-elegibilidad`, {
                reglas: reglasEdit, motivo: motivo || "Actualización desde UI",
            });
            toast.success(`Reglas v${(activa.version || 0) + 1} activadas`);
            setEdit(false);
            setMotivo("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const evaluar = async () => {
        if (!corteId) return toast.error("Selecciona corte");
        setBusy(true);
        try {
            const { data } = await api.post(
                `/facilities/${facility.id}/reglas-elegibilidad/evaluar`,
                null, { params: { corte_id: corteId } },
            );
            setEvaluacion(data);
            toast.success(`Evaluación: ${data.resultados_totales} créditos · elegible ${money(data.total_elegible, facility.moneda)}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="tab-elegibilidad-content">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-2xl flex items-center gap-2">
                        <Filter className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                        Reglas de elegibilidad
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Versión activa: <span className="badge-gold">v{activa.version}</span> · {activa.reglas?.length || 0} reglas.
                        Al guardar cambios se crea una nueva versión (append-only).
                    </p>
                </div>
                {canEdit && !edit && (
                    <Button onClick={startEdit} data-testid="editar-reglas-btn">
                        Editar reglas (nueva versión)
                    </Button>
                )}
            </div>

            {!edit ? (
                <div className="rounded-md border border-border bg-card overflow-hidden">
                    <table className="trf-table" data-testid="reglas-activas-table">
                        <thead>
                            <tr>
                                <th>Código</th><th>Nombre</th><th>Tipo</th>
                                <th>Parámetros</th><th>Severidad</th><th>Haircut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(activa.reglas || []).map((r) => (
                                <tr key={r.codigo}>
                                    <td className="font-medium">{r.codigo}</td>
                                    <td>{r.nombre}</td>
                                    <td><span className="badge-gold">{r.tipo}</span></td>
                                    <td className="text-xs hash-cell">{JSON.stringify(r.parametros)}</td>
                                    <td>
                                        <span className={r.severidad === "hard" ? "badge-error" : "badge-gold"}>
                                            {r.severidad}
                                        </span>
                                    </td>
                                    <td className="tabular">{r.haircut_pct !== null && r.haircut_pct !== undefined ? `${r.haircut_pct}%` : "—"}</td>
                                </tr>
                            ))}
                            {(!activa.reglas || activa.reglas.length === 0) && (
                                <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">
                                    Sin reglas configuradas. {canEdit && "Crea la primera versión."}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="rounded-md border border-border bg-card p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Editor · nueva versión v{(activa.version || 0) + 1}</div>
                        <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>Cancelar</Button>
                    </div>
                    <div>
                        <Label>Motivo del cambio</Label>
                        <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                            data-testid="motivo-reglas" />
                    </div>
                    <table className="trf-table text-xs" data-testid="reglas-editor-table">
                        <thead>
                            <tr>
                                <th>Código</th><th>Nombre</th><th>Tipo</th>
                                <th>Parámetros (JSON)</th><th>Severidad</th><th>Haircut %</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {reglasEdit.map((r, i) => (
                                <tr key={i}>
                                    <td><Input value={r.codigo} className="w-28"
                                        onChange={(e) => setRegla(i, "codigo", e.target.value)} /></td>
                                    <td><Input value={r.nombre} className="w-48"
                                        onChange={(e) => setRegla(i, "nombre", e.target.value)} /></td>
                                    <td>
                                        <select className="border border-border rounded-sm px-1 py-1 bg-background text-xs"
                                            value={r.tipo}
                                            onChange={(e) => setRegla(i, "tipo", e.target.value) || setRegla(i, "parametros", tipoDefaults(e.target.value))}>
                                            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        <Input value={JSON.stringify(r.parametros)}
                                            className="w-44 font-mono text-[10px]"
                                            onChange={(e) => {
                                                try { setRegla(i, "parametros", JSON.parse(e.target.value)); } catch {}
                                            }} />
                                    </td>
                                    <td>
                                        <select className="border border-border rounded-sm px-1 py-1 bg-background text-xs"
                                            value={r.severidad}
                                            onChange={(e) => setRegla(i, "severidad", e.target.value)}>
                                            {SEVERIDADES.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </td>
                                    <td><Input type="number" value={r.haircut_pct ?? ""} className="w-20"
                                        onChange={(e) => setRegla(i, "haircut_pct", e.target.value === "" ? null : Number(e.target.value))} /></td>
                                    <td><Button size="sm" variant="ghost" onClick={() => rmRegla(i)}><X className="h-3 w-3" /></Button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={addRegla} data-testid="add-regla-btn">
                            <Plus className="h-3 w-3 mr-1" /> Añadir regla
                        </Button>
                        <Button size="sm" onClick={saveReglas} disabled={busy || reglasEdit.length === 0}
                            data-testid="submit-reglas">
                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Guardar nueva versión"}
                        </Button>
                    </div>
                </div>
            )}

            {/* Evaluar */}
            <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="evaluar-elegibilidad-panel">
                <h4 className="font-display text-lg">Ejecutar motor de elegibilidad</h4>
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="min-w-[240px]">
                        <Label>Corte de tape</Label>
                        <Select value={corteId} onValueChange={setCorteId}>
                            <SelectTrigger data-testid="corte-select"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                            <SelectContent>
                                {cortes.map((c) => (
                                    <SelectItem key={c.corte_id} value={c.corte_id}>
                                        {c.fecha_corte} · {c.registros_validos} filas
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={evaluar} disabled={busy || !corteId || !activa.reglas?.length}
                        data-testid="evaluar-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Play className="h-4 w-4 mr-1" /> Evaluar</>)}
                    </Button>
                </div>
                {evaluacion && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-border" data-testid="evaluacion-result">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Créditos evaluados</div>
                            <div className="font-display text-xl">{evaluacion.resultados_totales}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Elegible neto</div>
                            <div className="font-display text-xl text-[color:var(--brand-exito)]">
                                {money(evaluacion.total_elegible, facility.moneda)}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Excluido</div>
                            <div className="font-display text-xl text-[color:var(--brand-coral)]">
                                {money(evaluacion.total_excluido, facility.moneda)}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Haircut aplicado</div>
                            <div className="font-display text-xl">{money(evaluacion.total_haircut, facility.moneda)}</div>
                        </div>
                    </div>
                )}
            </div>

            {historico.length > 0 && (
                <div className="rounded-md border border-border bg-card overflow-hidden">
                    <div className="px-4 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">
                        Histórico de evaluaciones
                    </div>
                    <table className="trf-table text-xs">
                        <thead><tr>
                            <th>Fecha</th><th>Corte</th><th>Versión reglas</th>
                            <th className="text-right">Créditos</th>
                            <th className="text-right">Elegible</th>
                            <th className="text-right">Excluido</th>
                        </tr></thead>
                        <tbody>
                            {historico.slice(0, 15).map((h) => (
                                <tr key={h.id} data-testid={`eval-hist-${h.id}`}>
                                    <td className="tabular">{h.calculado_at?.slice(0, 16)}</td>
                                    <td className="hash-cell">{h.corte_id?.slice(0, 10)}…</td>
                                    <td>v{h.reglas_version}</td>
                                    <td className="text-right tabular">{h.total_creditos}</td>
                                    <td className="text-right tabular">{money(h.total_elegible, facility.moneda)}</td>
                                    <td className="text-right tabular">{money(h.total_excluido, facility.moneda)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
